import { Injectable } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "./dialog.service";
import {
  GeneralConfirmationModalComponent
} from "../components/general-confirmation-modal/general-confirmation-modal.component";
import { GeneralConfirmationModalData, GeneralConfirmationModalResult } from "../models";

/** Failures that are not an HTTP status but still need to be explained to the user. */
export type DocumentViewerErrorCode =
  'NO_CONTENT'      // the backend answered but returned no document / no location
  | 'INVALID_CONTENT' // the payload could not be decoded into a file
  | 'POPUP_BLOCKED'   // the browser refused to open the tab holding the document
  | 'NETWORK'         // the request never reached the server
  | 'UNKNOWN';

export class DocumentViewerError extends Error {
  constructor(public readonly code: DocumentViewerErrorCode, public readonly status?: number) {
    super(`Document could not be opened (${status ?? code})`);
  }
}

export interface DocumentViewerOptions {
  /** Name suggested when the file has to be downloaded instead of shown. */
  fileName?: string;
  /**
   * Translation keys replacing the default message for the given HTTP statuses,
   * for endpoints where a status does not carry its usual meaning — e.g. the
   * invoice PDF endpoint answers 403 for an unknown *and* for a foreign invoice,
   * which is not a permission problem the customer can act on.
   */
  errorMessageKeys?: Record<number, string>;
}

/** Body of an endpoint that hands the document over as base64 instead of a URL. */
export interface Base64Document {
  name?: string;
  size?: number;
  content?: string;
}

interface DetectedFormat {
  mime: string;
  extension: string;
  /**
   * `true`  – every browser renders it in a tab.
   * `false` – no browser renders it, download straight away.
   * `probe` – depends on the browser (HEIC in Safari vs. Chrome), so we test the
   *           actual bytes with `createImageBitmap` before committing to a tab.
   */
  inline: boolean | 'probe';
}

const UNKNOWN_FORMAT: DetectedFormat = { mime: 'application/octet-stream', extension: '', inline: false };

/**
 * Opens documents in a new browser tab and makes every failure visible: instead
 * of a blank tab or a silent `return`, the user gets a dialog with a readable
 * reason and the technical code (HTTP status where we have one).
 *
 * Uploads are not restricted to PDFs (the FNOL file input has no `accept`
 * filter), so the actual format is sniffed from the bytes rather than assumed.
 * Formats the browser cannot render — HEIC outside Safari, TIFF, anything
 * unrecognised — are downloaded instead, with a dialog saying so.
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentViewerService {
  constructor(private dialogService: DialogService,
              private translate: TranslateService) {
  }

  /**
   * Opens a remote document. `resolveUrl` returns the (presigned) URL of the
   * file. The bytes are fetched and re-wrapped in a blob with the detected
   * content type, because such URLs carry no inline content-type: navigating to
   * one directly downloads the file instead of rendering it.
   */
  async openRemoteDocument(resolveUrl: () => Promise<string | undefined>, options: DocumentViewerOptions = {}): Promise<void> {
    // Open the tab synchronously so it is not blocked by the popup blocker,
    // then point it at the document once it resolves.
    const tab = window.open('', '_blank');

    try {
      const url = await this.run(resolveUrl);
      if (!url) {
        throw new DocumentViewerError('NO_CONTENT');
      }

      const response = await this.run(() => fetch(url));
      if (!response.ok) {
        throw new DocumentViewerError('UNKNOWN', response.status);
      }

      await this.show(await response.blob(), options, tab);
    } catch (e) {
      tab?.close();
      this.showError(e, options);
    }
  }

  /**
   * Opens a document the backend hands over as base64. `resolve` performs the
   * request.
   *
   * Unlike `openRemoteDocument`, no tab is opened up front: the caller shows the
   * wait in the page itself, and a blank tab sitting there for the seconds the
   * request takes is worse than none. The tab is opened once the bytes are in
   * hand — which the browser may no longer attribute to the click, so a refused
   * tab falls back to saving the file rather than losing the document.
   */
  async openRemoteBase64Document(resolve: () => Promise<Base64Document | undefined>,
                                 options: DocumentViewerOptions = {}): Promise<void> {
    try {
      const payload = await this.run(resolve);
      if (!payload?.content) {
        throw new DocumentViewerError('NO_CONTENT');
      }

      const blob = new Blob([this.decode(payload.content)]);
      const named = { ...options, fileName: payload.name || options.fileName };

      try {
        await this.show(blob, named);
      } catch (e) {
        if (!(e instanceof DocumentViewerError) || e.code !== 'POPUP_BLOCKED') {
          throw e;
        }
        this.download(blob, await this.detectFormat(blob), named);
        this.showPopupBlockedNotice();
      }
    } catch (e) {
      this.showError(e, options);
    }
  }

  private decode(base64: string): Uint8Array<ArrayBuffer> {
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return bytes;
    } catch {
      throw new DocumentViewerError('INVALID_CONTENT');
    }
  }

  /**
   * Shows the blob in the pre-opened tab (or a new one), falling back to a
   * download when the browser cannot render the format.
   */
  private async show(blob: Blob, options: DocumentViewerOptions, tab?: Window | null): Promise<void> {
    const format = await this.detectFormat(blob);

    if (!await this.canRenderInline(blob, format)) {
      tab?.close();
      this.download(blob, format, options);
      this.showNotice(format);
      return;
    }

    const objectUrl = URL.createObjectURL(new Blob([blob], { type: format.mime }));

    if (tab) {
      tab.location.href = objectUrl;
      return;
    }

    if (!window.open(objectUrl, '_blank')) {
      URL.revokeObjectURL(objectUrl);
      throw new DocumentViewerError('POPUP_BLOCKED');
    }
  }

  /**
   * Identifies the format from its magic bytes. The backend stores no content
   * type, and `blob.type` coming off S3 is usually `application/octet-stream`,
   * so the bytes are the only reliable source.
   */
  private async detectFormat(blob: Blob): Promise<DetectedFormat> {
    const header = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    const startsWith = (...bytes: number[]) => bytes.every((b, i) => header[i] === b);
    const ascii = (offset: number, length: number) =>
      String.fromCharCode(...header.slice(offset, offset + length));

    if (startsWith(0x25, 0x50, 0x44, 0x46)) { // %PDF
      return { mime: 'application/pdf', extension: 'pdf', inline: true };
    }
    if (startsWith(0xFF, 0xD8, 0xFF)) {
      return { mime: 'image/jpeg', extension: 'jpg', inline: true };
    }
    if (startsWith(0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A)) {
      return { mime: 'image/png', extension: 'png', inline: true };
    }
    if (ascii(0, 4) === 'GIF8') {
      return { mime: 'image/gif', extension: 'gif', inline: true };
    }
    if (ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') {
      return { mime: 'image/webp', extension: 'webp', inline: true };
    }
    if (startsWith(0x49, 0x49, 0x2A, 0x00) || startsWith(0x4D, 0x4D, 0x00, 0x2A)) {
      // No browser renders TIFF in a tab.
      return { mime: 'image/tiff', extension: 'tiff', inline: false };
    }
    if (ascii(4, 4) === 'ftyp') {
      // ISO base media container: the brand says which flavour it is.
      const brand = ascii(8, 4);
      if (brand === 'avif' || brand === 'avis') {
        return { mime: 'image/avif', extension: 'avif', inline: 'probe' };
      }
      if (brand.startsWith('hei') || brand.startsWith('hev') || brand === 'mif1' || brand === 'msf1') {
        // Rendered by Safari, not by Chrome or Firefox.
        return { mime: brand === 'msf1' ? 'image/heif-sequence' : 'image/heic', extension: 'heic', inline: 'probe' };
      }
      if (brand.startsWith('mp4') || brand === 'isom' || brand === 'qt  ') {
        return { mime: 'video/mp4', extension: 'mp4', inline: true };
      }
    }

    return UNKNOWN_FORMAT;
  }

  /**
   * For formats whose support differs per browser, the only trustworthy answer
   * comes from asking the browser to decode the real bytes.
   */
  private async canRenderInline(blob: Blob, format: DetectedFormat): Promise<boolean> {
    if (format.inline !== 'probe') {
      return format.inline;
    }
    if (typeof createImageBitmap !== 'function') {
      return false;
    }

    try {
      const bitmap = await createImageBitmap(new Blob([blob], { type: format.mime }));
      bitmap.close();
      return true;
    } catch {
      return false;
    }
  }

  /** Saves the file locally, since it cannot be displayed. */
  private download(blob: Blob, format: DetectedFormat, options: DocumentViewerOptions): void {
    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = this.buildFileName(format, options);
    link.click();

    // The download is queued synchronously, so the URL can be released once the
    // current task is done; keeping it alive would leak the whole file.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }

  private buildFileName(format: DetectedFormat, options: DocumentViewerOptions): string {
    const base = (options.fileName || 'document')
      .split(/[\\/]/).pop()!
      .replace(/\.[^.]+$/, '')
      .replace(/[^\w.\-]+/g, '-') || 'document';

    return format.extension ? `${base}.${format.extension}` : base;
  }

  /** Runs a request and normalises whatever it throws into a DocumentViewerError. */
  private async run<T>(request: () => Promise<T>): Promise<T> {
    try {
      return await request();
    } catch (e: any) {
      const status = this.extractStatus(e);
      if (status) {
        throw new DocumentViewerError('UNKNOWN', status);
      }
      // `fetch` rejects only when the request itself failed (offline, CORS, DNS).
      throw new DocumentViewerError('NETWORK');
    }
  }

  /** Digs the HTTP status out of an Amplify ApiError, a fetch Response error or a plain object. */
  private extractStatus(e: any): number | undefined {
    const status = e?.response?.statusCode
      ?? e?.response?.status
      ?? e?.$metadata?.httpStatusCode
      ?? e?.statusCode
      ?? e?.status;

    return typeof status === 'number' ? status : undefined;
  }

  /** Tells the user the file was downloaded because the browser cannot show it. */
  private showNotice(format: DetectedFormat): void {
    const key = format === UNKNOWN_FORMAT
      ? 'document-viewer.downloaded.unsupported-format'
      : 'document-viewer.downloaded.unsupported-type';

    this.openDialog(
      this.translate.instant('document-viewer.downloaded.title'),
      this.translate.instant(key, { type: format.extension.toUpperCase() }),
      format.extension ? format.extension.toUpperCase() : 'UNKNOWN'
    );
  }

  /** Tells the user the file was saved because the browser refused the tab. */
  private showPopupBlockedNotice(): void {
    this.openDialog(
      this.translate.instant('document-viewer.downloaded.title'),
      this.translate.instant('document-viewer.downloaded.popup-blocked'),
      'POPUP_BLOCKED'
    );
  }

  private showError(e: unknown, options: DocumentViewerOptions = {}): void {
    console.error(e);

    const error = e instanceof DocumentViewerError ? e : new DocumentViewerError('UNKNOWN');

    this.openDialog(
      this.translate.instant('document-viewer.error.title'),
      this.translate.instant(this.messageKey(error, options)),
      error.status ? error.status.toString() : error.code
    );
  }

  private openDialog(title: string, message: string, detail: string): void {
    this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title,
        message: `${message}<br><span class="detail">${this.translate.instant('document-viewer.error.code', { code: detail })}</span>`,
        useInnerHtml: true,
        confirmButton: GeneralConfirmationModalResult.Confirm,
        confirmButtonText: 'OK'
      } as GeneralConfirmationModalData
    });
  }

  private messageKey(error: DocumentViewerError, options: DocumentViewerOptions): string {
    const override = error.status === undefined ? undefined : options.errorMessageKeys?.[error.status];
    if (override) {
      return override;
    }

    if (error.status === 401 || error.status === 403) {
      return 'document-viewer.error.forbidden';
    }
    if (error.status === 404) {
      return 'document-viewer.error.not-found';
    }
    if (error.status && error.status >= 500) {
      return 'document-viewer.error.server';
    }

    switch (error.code) {
      case 'NO_CONTENT':
        return 'document-viewer.error.no-content';
      case 'INVALID_CONTENT':
        return 'document-viewer.error.invalid-content';
      case 'POPUP_BLOCKED':
        return 'document-viewer.error.popup-blocked';
      case 'NETWORK':
        return 'document-viewer.error.network';
      default:
        return 'document-viewer.error.unknown';
    }
  }
}
